import React, {useEffect, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {
  Flex,
  Select,
  Table,
  message,
  Button,
  Checkbox,
  Upload,
  UploadProps,
  GetProp, Form
} from "antd";
import Title from "antd/es/typography/Title";
import ButtonBlack from "../../ui/buttons/ButtonBlack";
import ButtonGreen from "../../ui/buttons/ButtonGreen";
import {Dayjs} from "dayjs";
import urls from "../../../api/urls";
import {api, HydraData} from "../../../api/apiProvider";
import useSWR from "swr";
import {useParams} from "react-router-dom";
import {BriefManager, BriefTaskKpi, BriefTaskKpiData, Kpi, Product, TaskStatus} from "../../../api/types";
import {DefaultOptionType} from "antd/es/select";
import IconUploadFile from "../../ui/icons/IconUploadFile";
import IconComments from "../../ui/icons/IconComments";
import Column from "antd/lib/table/Column";
import useSWRMutation from "swr/mutation";
import ButtonRed from "../../ui/buttons/ButtonRed";
import KpiDataFile from "./KpiDataFile";
import {isEqualValues} from "../../../utils";

type FileType = Parameters<GetProp<UploadProps, 'beforeUpload'>>[0]

interface Item {
  id: number;
  kpi: string;
  briefKpiTaskData: BriefTaskKpiData[];
  period: Dayjs[];
  manager: string;
  commentary: string;
}

interface Model {
  id: string,
  '@id': string,
  name: string,
}

interface BriefDataType {
  '@context': string,
  '@id': string,
  '@type': string,
  products: Product[],
  commentary: string,
  dataDateEnd: string,
  dataDateStart: string,
  dateEnd: string,
  id: string;
  manager?: string,
  name: string,
  product: Model,
  kpi: Kpi,
  seasonalityText: string,
  tasks: string[],
}

interface DataType extends Item {
  key: string;
  dataIndex: string;
}

interface KpiDataProps {
  brief: BriefDataType,
  setSelectedCommentaryId: Function,
  selectedCommentaryId: Number,
}

const updateBriefKpiTask = async (id: number, data: any) => {
  return await api.patch(`${urls.briefTaskKpis}/${id}`, {arg: data})
}

const deleteBriefKpiTask = async (id: number) => {
  return await api.delete(`${urls.briefTaskKpis}/${id}`)
}

const addBriefKpiTaskData = async (data: any) => {
  return await api.post(urls.briefTaskKpiData, {arg: data})
}

const updateBriefKpiTaskData = async (id: number, data: any) => {
  return await api.patch(`${urls.briefTaskKpiData}/${id}`, {arg: data})
}

const uploadFile = async (formData: any) => {
  return await api.postFile(`${urls.mediaObjects}`, {arg: formData})
}

export default function KpiData({
                                  brief,
                                  selectedCommentaryId, setSelectedCommentaryId
                                }: KpiDataProps) {
  const {t} = useTranslation()
  const routeParams = useParams()
  const briefId = Number(routeParams.id) || 0
  const [kpiOptions, setKpiOptions] = useState<DefaultOptionType[]>([])
  const [managersOptions, setManagersOptions] = useState<DefaultOptionType[]>([])
  const [statusesOptions, setStatusesOptions] = useState<DefaultOptionType[]>([])
  const [form] = Form.useForm();
  const [fileList, setFileList] = useState<Record<number, boolean>>([])
  const [selectedChannelsIds, setSelectedChannelsIds] = useState<number[]>([])
  const [isDataSaving, setIsDataSaving] = useState<boolean>(false)
  const [initialValues, setInitialValues] = useState<FormData[]>([])
  const [isValuesChanged, setIsValuesChanged] = useState<boolean>(false)

  const {
    data: briefTaskKpis,
    mutate: mutateBriefTaskKpis,
  } = useSWR<{
    data: BriefTaskKpi[]
  }>(`${urls.briefTaskKpis}?brief.id=${brief.id}&t[]=taskKpiBrief&t[]=dict&t[]=briefKpiTaskData&t[]=media_object:read`, api.get, {
    revalidateOnFocus: false,
  })

  const {
    data: managers,
    isValidating: isManagersValidating,
  } = useSWR<HydraData>(`${urls.briefs}/${briefId}/managers?t[]=manager`, api.get, {
    revalidateOnFocus: false
  })

  const {
    data: statuses,
  } = useSWR<HydraData>(urls.tasksStatuses, api.get, {
    revalidateOnFocus: false
  })

  const {
    trigger: createBriefKpiTask,
    isMutating: isBriefKpiTaskCreating
  } = useSWRMutation(urls.briefTaskKpis, api.post)

  const {
    data: kpis,
  } = useSWR<{
    data: Kpi[]
  }>(urls.kpis, api.get, {
    revalidateOnFocus: false,
  })

  useEffect(() => {
    if (kpis && kpis.data) {
      setKpiOptions(kpis.data.map((kpi: Kpi) => ({
        value: kpi['@id'],
        label: kpi.name,
      })))
    }
  }, [kpis])

  useEffect(() => {
    if (managers && managers.data) {
      setManagersOptions(managers.data.map((manager: BriefManager) => ({
        label: manager.manager.email,
        value: manager.manager['@id'],
      })))
    }
  }, [managers])

  useEffect(() => {
    if (statuses && statuses.data) {
      setStatusesOptions(statuses.data.map((status: TaskStatus) => ({
        label: status.name,
        value: status['@id'],
      })))
    }
  }, [statuses])

  useEffect(() => {
    if (briefTaskKpis && briefTaskKpis.data) {
      const newFactors = briefTaskKpis.data.map((briefTaskKpi: BriefTaskKpi) => ({
        id: briefTaskKpi.id,
        name: briefTaskKpi.name,
        kpi: {
          value: briefTaskKpi.kpi["@id"],
          label: briefTaskKpi.kpi.name,
        },
        //briefKpiTaskData: briefTaskKpi.briefKpiTaskData,
        //period: [dayjs(briefTaskKpi.dataDateStart), dayjs(briefTaskKpi.dataDateEnd)],
        manager: briefTaskKpi.manager,
        source: '',
        commentary: briefTaskKpi.commentary,
        key: String(briefTaskKpi.id),
        dataIndex: '1',
        taskDataId: briefTaskKpi.briefKpiTaskData && briefTaskKpi.briefKpiTaskData[0]
          && briefTaskKpi.briefKpiTaskData[0].id || null,
        file: briefTaskKpi.briefKpiTaskData && briefTaskKpi.briefKpiTaskData[0]
        && briefTaskKpi.briefKpiTaskData[0].file ? briefTaskKpi.briefKpiTaskData[0].file : null,
        status: briefTaskKpi.briefKpiTaskData && briefTaskKpi.briefKpiTaskData[0]
        && briefTaskKpi.briefKpiTaskData[0].status ? briefTaskKpi.briefKpiTaskData[0].status : null,
      }))

      const files: Record<number, boolean> = []
      for (let key in briefTaskKpis.data) {
        const briefTaskKpi = briefTaskKpis.data[key]
        if (!briefTaskKpi.briefKpiTaskData) {
          continue
        }
        for (let taskData of briefTaskKpi.briefKpiTaskData) {
          if (taskData.file) {
            files[key] = true
          }
        }
      }

      setFileList(files)

      form.setFieldValue('factors', newFactors)
    }
  }, [briefTaskKpis])

  const onFinish = async (values: any) => {
    setIsDataSaving(true)
    for (let index in values.factors) {
      const kpiData = values.factors[index]
      const data = {
        name: `Задача на сбор данных по каналу ${'channelName'}`,
        manager: kpiData.manager,
        kpi: kpiData.kpi.value,
        brief: `api${urls.briefs}/${briefId}`,
        dataDateStart: brief.dataDateStart,
        dataDateEnd: brief.dataDateEnd,
      }

      let mediaFactorId = kpiData.id

      if (mediaFactorId > 0) {
        await updateBriefKpiTask(mediaFactorId, data).catch(errors => {
          if (errors['hydra:description']) {
            message.error(errors['hydra:description'])
          }
        })
      } else {
        const response = await createBriefKpiTask(data).then(response => response).catch(errors => {
          if (errors['hydra:description']) {
            message.error(errors['hydra:description'])
          }
        })

        if (response && response.id) {
          mediaFactorId = response.id
        }
      }

      if (!mediaFactorId) {
        setIsDataSaving(false)
        return
      }

      let fileId = kpiData.file && kpiData.file['@id'] ? kpiData.file['@id'] : null

      if (kpiData.file && kpiData.file[0] && kpiData.file[0].uid) {
        const formData = new FormData()

        formData.append('file', kpiData.file[0] as FileType);

        const response = await uploadFile(formData).then(response => response).catch(errors => {
          if (errors['hydra:description']) {
            message.error(errors['hydra:description'])
          }
          setIsDataSaving(false)
          return
        })

        if (response && response['@id']) {
          fileId = response['@id']
        }
      }

      if (kpiData.taskDataId) {
        await updateBriefKpiTaskData(kpiData.taskDataId, {
          status: kpiData.status,
          file: kpiData.file && kpiData.file['@id']
            ? kpiData.file['@id']
            : fileId,
        })
      } else {
        await addBriefKpiTaskData({
          file: fileId,
          status: kpiData.status,
          briefKpiTask: `/api${urls.briefTaskKpis}/${mediaFactorId}`,
        }).catch(errors => {
          if (errors['hydra:description']) {
            message.error(errors['hydra:description'])
          }
        })
      }
    }

    message.success(t('common.saved'))
    setFileList([])
    mutateBriefTaskKpis()
    setIsValuesChanged(false)
    setIsDataSaving(false)
  }

  const deleteTasks = async () => {
    let deletedCount = 0
    let factors = form.getFieldValue('factors')

    for (let index of selectedChannelsIds) {
      const record = factors[index]
      if (record && record.id) {
        await deleteBriefKpiTask(record.id).then(() => {
          deletedCount++
        }).catch(errors => {
          if (errors['hydra:description']) {
            message.error(errors['hydra:description'])
          }

          return
        })
      } else {
        factors.splice(index, 1)
      }
    }

    form.setFieldValue('factors', factors)

    if (deletedCount > 0) {
      message.success(t('common.deleted'))
      mutateBriefTaskKpis()
    }

    setSelectedChannelsIds([])
  }

  const getRowClassName = (index: number, record: any) => {
    if (selectedChannelsIds.includes(index)) {
      return 'selected-task-row'
    }

    const val = form.getFieldValue(['factors', index, "status"])
    const status = statuses && statuses.data.find((item: TaskStatus) => {
      return item["@id"] === val
    })

    if (status && status.name === 'принято') {
      return 'accepted-task-row'
    }

    return ''
  }

  const onValuesChange = (allValues: any) => {
    if (allValues && allValues.factors && allValues.factors.length > 0) {
      const item = allValues.factors[0]

      if (item && item.type && item.type === 'click') {
        form.setFieldValue('factors', [
          {
            kpi: {
              value: brief.kpi['@id'],
              label: brief.kpi.name,
            }
          }
        ])
      }
    }

    if (allValues.length !== initialValues?.length) {
      setIsValuesChanged(true)
      return
    }

    for (let key in initialValues) {
      if (!initialValues.hasOwnProperty(key)) {
        continue
      }

      const initialValue = initialValues[Number(key)]
      if (key in allValues) {
        const changedValue = allValues[key]

        if (!isEqualValues(initialValue, changedValue)) {
          setIsValuesChanged(true)
          return
        }
      }
    }

    setIsValuesChanged(false)
  }

  const isKpiData = () => {
    const factors = form.getFieldValue('factors')
    return factors && factors.length > 0
  }

  return (
    <Flex vertical gap={'middle'}>
      <Title level={5}>{t('managerTasks.kpiData')}</Title>
      <Form
        form={form}
        onFinish={onFinish}
        onValuesChange={onValuesChange}
      >
        <Form.List name="factors">
          {(factors, {add, remove}) => {
            return <Table
              bordered
              dataSource={factors}
              rowKey={'key'}
              scroll={{x: 800}}
              pagination={false}
              size={'middle'}
              footer={() => {
                return (
                  <Form.Item>
                    <Flex justify={'space-between'}>
                      <Flex gap={'small'}>
                        <div/>
                        <>
                          {!isKpiData() &&
                            <ButtonGreen onClick={add}>{t('common.add')}</ButtonGreen>
                          }
                          {selectedChannelsIds && selectedChannelsIds.length > 0 &&
                            <ButtonRed onClick={deleteTasks}>{t('common.delete')}</ButtonRed>
                          }
                        </>
                      </Flex>
                      <ButtonBlack
                        onClick={form.submit}
                        loading={isDataSaving}
                        disabled={!isValuesChanged}
                      >{t('common.save')}</ButtonBlack>
                    </Flex>
                  </Form.Item>
                )
              }}
              rowClassName={(record, index) => getRowClassName(index, record)}
            >
              <Column
                dataIndex={"id"}
                title={"№"}
                render={
                  (value: any, record: DataType, index: number) => {
                    return <div>{index + 1}</div>
                  }
                }
                width={50}
              />
              <Column
                dataIndex={"actions"}
                title={""}
                render={
                  (value: any, record: DataType, index: number) => {
                    return <Checkbox
                      checked={selectedChannelsIds.includes(index)}
                      onChange={(event) => {
                        if (event.target.checked) {
                          setSelectedChannelsIds([
                            ...selectedChannelsIds,
                            index,
                          ])
                        } else {
                          setSelectedChannelsIds([...selectedChannelsIds.filter(function (value, valueIndex, arr) {
                            return value !== index
                          })])
                        }
                      }}
                    />
                  }
                }
                width={30}
              />
              <Column
                dataIndex={"kpi"}
                title={"Kpi"}
                render={
                  (value: any, record: DataType, index: number) => {
                    return (<Form.Item name={[index, "kpi"]} rules={[{required: true, message: t('common.choose')}]}>
                      <Select
                        value={value}
                        style={{width: '100%'}}
                        disabled
                      />
                    </Form.Item>)
                  }
                }
                width={400}
              />
              <Column
                dataIndex={"manager"}
                title={t('managerTasks.responsible')}
                render={
                  (value: any, record: DataType, index: number) => {
                    return (
                      <Form.Item name={[index, "manager"]} rules={[{required: true, message: t('common.choose')}]}>
                        <Select
                          value={value}
                          style={{width: '100%'}}
                          options={managersOptions}
                        />
                      </Form.Item>)
                  }
                }
                width={400}
              />
              <Column
                dataIndex={"file"}
                title={t('managerTasks.dataSource')}
                render={
                  (value: any, record: DataType, index: number) => {
                    const file = form.getFieldValue(['factors', index, "file"])

                    return <Flex vertical gap={'small'}
                                 id={'file-wrapper' + index}
                    >
                      <Form.Item
                        shouldUpdate
                      >
                        {() => {

                          return (
                            <>
                              {file && file.id &&
                                <div key={file.id} style={{height: '32px'}}>
                                  <KpiDataFile
                                    key={String(index)}
                                    fileUrl={file.contentUrl}
                                    fileName={file.originalName}
                                    inputId={'file-wrapper' + index}
                                    onRemove={() => {
                                      form.setFieldValue(['factors', index, "file"], null)
                                      const newFileList = fileList
                                      newFileList[index] = false
                                      setFileList(newFileList)
                                      form.setFieldsValue({})
                                      setIsValuesChanged(true)
                                    }}
                                    editable
                                  />
                                </div>
                              }
                              {(!file || fileList[index]) &&
                                <Upload
                                  id={'file' + index}
                                  beforeUpload={file => {
                                    form.setFieldValue(['factors', index, "file"], [file])
                                    const newFileList = fileList;
                                    newFileList[index] = true
                                    setFileList(newFileList)
                                    form.setFieldsValue({})
                                    setIsValuesChanged(true)
                                    return false
                                  }}
                                  onRemove={() => {
                                    setIsValuesChanged(true)
                                  }}
                                  maxCount={1}
                                  style={{width: '100%'}}
                                  accept=".csv, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, application/vnd.ms-excel"
                                  itemRender={(originNode, file) => {
                                    return fileList[index] ? <KpiDataFile
                                      key={String(index)}
                                      fileUrl={file.url}
                                      fileName={file.name}
                                      inputId={'file-wrapper' + index}
                                      onRemove={() => {
                                        form.setFieldValue(['factors', index, "file"], null)
                                        const newFileList = fileList
                                        newFileList[index] = false
                                        setFileList(newFileList)
                                        form.setFieldsValue({})
                                      }}
                                      editable
                                    /> : ''
                                  }}
                                >
                                  {!fileList[index] &&
                                    <Button icon={<IconUploadFile/>} size={'small'}>{t('managerTasks.upload')}</Button>
                                  }

                                </Upload>
                              }
                            </>
                          )
                        }}

                      </Form.Item>
                    </Flex>
                  }
                }
                width={212}
              />
              <Column
                dataIndex={"status"}
                title={t('managerTasks.status')}
                render={
                  (value: any, record: DataType, index: number) => {
                    return (<Form.Item name={[index, "status"]} rules={[{required: true, message: t('common.choose')}]}>
                      <Select
                        value={value}
                        style={{width: 160}}
                        options={statusesOptions}
                      />
                    </Form.Item>)
                  }
                }
                fixed={'right'}
                width={170}
              />
              <Column
                dataIndex={"commentary"}
                title={t('managerTasks.comments')}
                render={
                  (value: any, record: DataType, index: number) => {
                    const item = form.getFieldValue(['factors', index, "commentary"])
                    return <>
                      {item &&
                        <div className={'media-factors__comments'}
                             onClick={() => {
                               if (Number(item.split('/').pop()) === selectedCommentaryId) {
                                 setSelectedCommentaryId(0)
                               } else {
                                 setSelectedCommentaryId(Number(item.split('/').pop()))
                               }
                             }}>
                          <IconComments/>
                        </div>
                      }
                    </>
                  }
                }
                fixed={'right'}
                width={100}
              />

            </Table>
          }}
        </Form.List>
      </Form>
    </Flex>
  )
}
